import React, { FC, memo, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  colors,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
  TextField,
  Tooltip,
  Button,
  Grid,
  InputAdornment,
  SvgIcon
} from '@material-ui/core';
import PerfectScrollbar from 'react-perfect-scrollbar';
import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { useInventoryAudit } from 'src/hooks/inventory-audit';
import { Alert } from '@material-ui/lab';
import { usePermissions } from 'src/hooks';
import { DecisionDialog } from 'src/components';
import useResolution from 'src/hooks/useResolution';
import { cloneDeep } from 'lodash';
import { MobileAuditListingRow } from './MobileAuditListingRow';
import { Search as SearchIcon } from 'react-feather';
import { GetProductWithTotalAvailableItemsData } from 'src/types';
import ClearIcon from '@material-ui/icons/Clear';

const useStyles = makeStyles({
  table: {
    borderCollapse: 'collapse',
    width: '100%'
  },
  tableCell: {
    border: '1px solid #ddd',
    padding: '8px'
  },
  tableHeader: {
    border: '1px solid #ddd',
    padding: '1rem',
    backgroundColor: '#f4f4f4',
    fontWeight: 'bold'
  },
  statusIcon: {
    marginRight: '8px'
  }
});

interface ScanSnProps {
  prodName?: string;
  scannedSn?: string[];
  id?: number;
  isMobile?: boolean;
}

export const ScanSerialNoTable: FC<ScanSnProps> = ({
  prodName,
  scannedSn = [],
  id
}) => {
  const { deleteSerialsForProduct } = useInventoryAudit();

  return (
    <Box>
      <Box>
        <Typography>{prodName}</Typography>
      </Box>

      {scannedSn?.[0] ? (
        <Box style={{ border: '0.5px solid #f4f4f4', padding: '.5rem' }}>
          {scannedSn?.map((sn, index) => (
            <Chip
              key={index}
              size={'medium'}
              onDelete={() => (id ? deleteSerialsForProduct(id, [sn]) : null)}
              style={{
                padding: '1rem',
                border: '1px solid gray',
                background: 'white',
                margin: 3
              }}
              label={sn}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

interface Props {}

const component: FC<Props> = () => {
  const { xs } = useResolution();
  const [serialNo, setSerialNo] = useState<string>('');

  const {
    inventoryAuditProductListing,
    hasAuditCountMismatch,
    commonAuditParams,
    isLoading,
    handleNonSnCountAudit,
    submitInventoryAuditLogs,
    getProductWithTotalAvailableItem,
    reApplyScannedSerialsAndCount
  } = useInventoryAudit();

  const classes = useStyles();
  const {
    canCreateInventoryLog,
    canSeeActualAvailableProductCount
  } = usePermissions();

  const [isAuditSubmit, setIsAuditSubmit] = useState<boolean>(false);
  //for filter purpose
  const [clonedInventoryAudit, setClonedInventoryAudit] = useState<
    GetProductWithTotalAvailableItemsData[]
  >([]);

  const handleAuditCountChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    product_id?: number
  ) => {
    const newValue = event.target.value;
    // Validate input to ensure it's a positive number
    if (/^\d*\.?\d*$/.test(newValue)) {
      handleNonSnCountAudit(product_id, parseInt(newValue));
    }
  };

  const handleSubmitAudit = () => {
    setIsAuditSubmit(true);
  };

  const refreshAuditCounts = async () => {
    const inventoryAuditProductListingClone =
      cloneDeep(inventoryAuditProductListing) ?? [];

    // coded like this for ts error
    const product_ids: number[] =
      inventoryAuditProductListingClone
        ?.filter((i) => !!i.id)
        .map((i) => i?.id || 0) || [];

    const res = await getProductWithTotalAvailableItem('all', {
      branch_id: commonAuditParams?.branch_id,
      category_id: commonAuditParams?.category_id,
      product_ids: product_ids
    });

    const newInventoryAuditProductListing = res?.originalData?.data;

    if (res?.success && newInventoryAuditProductListing?.[0]) {
      // use old state to get serials per product and re apply
      reApplyScannedSerialsAndCount(
        inventoryAuditProductListingClone,
        newInventoryAuditProductListing
      );
    } else {
      console.error('Fail to refetch audit counts');
    }
  };

  const onHandleFindSn = (
    serial?: string,
    inventoryAuditListingState?: GetProductWithTotalAvailableItemsData[]
  ) => {
    if (
      serial &&
      inventoryAuditListingState &&
      inventoryAuditListingState?.length > 0
    ) {
      const filteredProducts = inventoryAuditListingState?.filter((p) =>
        p?.scanned_serial_nos?.some((sn) =>
          sn?.toLowerCase().includes(serial.toLowerCase())
        )
      );

      setClonedInventoryAudit(filteredProducts);
    } else {
      setClonedInventoryAudit(inventoryAuditListingState || []);
    }
  };

  const hasInventoryAuditProductListing = !!inventoryAuditProductListing?.[0];

  //initialize dummy state
  useEffect(() => {
    if (
      inventoryAuditProductListing &&
      inventoryAuditProductListing?.length > 0
    ) {
      if (serialNo) {
        onHandleFindSn(serialNo, inventoryAuditProductListing);
        return;
      }
      setClonedInventoryAudit(cloneDeep(inventoryAuditProductListing));
    }
  }, [inventoryAuditProductListing, serialNo]);

  const AuditActionButtons = () => (
    <Grid container style={{ gap: '1em', marginTop: '1em' }}>
      <Grid item sm xs={12}>
        <Button
          fullWidth
          variant="outlined"
          color="primary"
          size="large"
          onClick={refreshAuditCounts}
          disabled={isLoading}
        >
          Refresh Audit counts
        </Button>
      </Grid>
      {canCreateInventoryLog && (
        <Grid item sm xs={12}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSubmitAudit}
            disabled={isLoading}
          >
            Submit Audit
          </Button>
        </Grid>
      )}
    </Grid>
  );

  return (
    <Box>
      {hasInventoryAuditProductListing ? (
        <>
          <Box
            sx={{
              mt: '1rem',
              display: 'flex',
              alignItems: 'flex-end',
              flexDirection: 'column'
            }}
          >
            {hasAuditCountMismatch && (
              <Alert severity="error" style={{ width: '100%' }}>
                {'There are some missing items...'}
              </Alert>
            )}
            <AuditActionButtons />
          </Box>
        </>
      ) : null}

      {hasInventoryAuditProductListing ? (
        <Grid container spacing={2} style={{ marginTop: '1rem' }}>
          <Grid item md={6} xs={12}>
            <TextField
              onChange={(e) => setSerialNo(e?.target?.value || '')}
              value={serialNo}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SvgIcon fontSize="small" color="action">
                      <SearchIcon />
                    </SvgIcon>
                  </InputAdornment>
                ),
                endAdornment: serialNo && (
                  <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                    <ClearIcon onClick={() => setSerialNo('')} />
                  </InputAdornment>
                )
              }}
              placeholder="Filter list by Serial No"
              variant="outlined"
            />
          </Grid>
        </Grid>
      ) : null}

      {hasInventoryAuditProductListing && !xs ? (
        <>
          <PerfectScrollbar style={{ marginTop: '1rem' }}>
            <TableContainer
              component={Paper}
              style={{
                minHeight: '50vh',
                maxHeight: '60vh',
                minWidth: 665,
                overflow: 'auto',
                marginBottom: '3rem'
              }}
            >
              <Table
                stickyHeader
                aria-label="sticky table"
                className={classes.table}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      className={classes.tableHeader}
                      style={{ width: '5%' }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      className={classes.tableHeader}
                      style={{ width: '65%' }}
                    >
                      Product Name
                    </TableCell>
                    <TableCell
                      className={classes.tableHeader}
                      style={{ width: '15%' }}
                    >
                      Audited Count
                    </TableCell>
                    {canSeeActualAvailableProductCount && (
                      <TableCell
                        className={classes.tableHeader}
                        style={{ width: '15%' }}
                      >
                        Available Items Count
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clonedInventoryAudit?.map((product, index) => (
                    <TableRow key={product?.id || index}>
                      <TableCell
                        className={classes.tableCell}
                        style={{ textAlign: 'center' }}
                      >
                        {product?.audited_count !==
                        product?.available_listing_count ? (
                          <WarningIcon
                            className={classes.statusIcon}
                            style={{ color: colors.yellow[800] }}
                          />
                        ) : (
                          <CheckCircleIcon
                            className={classes.statusIcon}
                            style={{ color: colors.green[800] }}
                          />
                        )}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {product?.is_serialized ? (
                          <Tooltip
                            title={`Kindly scan the serial no for ${product?.product_name} to update the audit count`}
                          >
                            <ScanSerialNoTable
                              id={product?.id}
                              prodName={product?.product_name}
                              scannedSn={product?.scanned_serial_nos}
                            />
                          </Tooltip>
                        ) : (
                          product?.product_name
                        )}
                      </TableCell>
                      <TableCell
                        className={classes.tableCell}
                        style={{ textAlign: 'center' }}
                      >
                        {!product?.is_serialized ? (
                          <TextField
                            variant="outlined"
                            id="audited-count"
                            type="number"
                            defaultValue={product?.audited_count || 0}
                            value={product?.audited_count}
                            onChange={(e: any) =>
                              handleAuditCountChange(e, product?.id)
                            }
                            InputLabelProps={{
                              shrink: true
                            }}
                            inputProps={{
                              step: 1,
                              min: 0,
                              max: product?.available_listing_count,
                              style: { textAlign: 'center' }
                            }}
                          />
                        ) : (
                          JSON.stringify(product?.audited_count)
                        )}
                      </TableCell>
                      {canSeeActualAvailableProductCount && (
                        <TableCell
                          className={classes.tableCell}
                          style={{ textAlign: 'center' }}
                        >
                          {product?.available_listing_count}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </PerfectScrollbar>
        </>
      ) : null}

      {hasInventoryAuditProductListing && xs ? (
        <Grid container style={{ marginTop: '1em' }}>
          {clonedInventoryAudit?.map((l, ind) => (
            <MobileAuditListingRow
              key={ind}
              ind={ind}
              id={l.id}
              canSeeCount={canSeeActualAvailableProductCount}
              auditedCount={l.audited_count}
              availableListingCount={l.available_listing_count}
              productName={l.product_name}
              scannedSerialNos={l.scanned_serial_nos}
              isSerialized={l.is_serialized}
              handleAuditCountChange={handleAuditCountChange}
            />
          ))}
        </Grid>
      ) : null}

      {!hasInventoryAuditProductListing ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            width: '100%',
            padding: '1rem'
          }}
        >
          <Typography
            variant="h5"
            style={{ color: 'gray', textAlign: 'center' }}
          >
            {`Set the branch and category filters to narrow down the products you want to audit.
             Then, click 'Select Products' to open a modal and choose items based on these filters.`}
          </Typography>
        </Box>
      ) : null}

      <DecisionDialog
        isOpen={isAuditSubmit}
        title="Create Audit Logs"
        subTitle={
          hasAuditCountMismatch
            ? 'There is still a discrepancy in audited listings, are you sure you want to submit this?'
            : `You are about to submit audit logs confirm action`
        }
        onHandleConfirmAction={submitInventoryAuditLogs}
        onHandleClose={() => setIsAuditSubmit(false)}
      />
    </Box>
  );
};

export const InventoryAuditListing = memo(component);
